import type { EnhancementPlugin } from 'src/components/GenerationTask/constants'
import { executeCreateChatWorkspace } from 'src/services/apollo/chatWorkspace'
import type { Task } from 'src/services/apollo/generated/graphql'
import { TaskStatus } from 'src/utils/task'
import { track } from 'src/utils/tracker'
import type { ConversationHistory } from './conversationAtom'
import { ConversationType } from './conversationAtom'

export const parseEnhancementDefaultArgs = (
  plugin: EnhancementPlugin,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Record<string, any> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const args = {} as Record<string, any>
  if (plugin.args) {
    for (const [name, config] of Object.entries(plugin.args)) {
      if (config.value) args[name] = config.value
    }
  }
  return args
}

export const extractEnhancementPlugin = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  parameters: any,
  plugins: {
    id: string
    label: string
    workflowName: string
    workflowVersion: string
  }[],
) => {
  const workflowName = parameters?.workflowName ?? ''
  if (workflowName) {
    const plugin = plugins.find(p => workflowName.startsWith(p.workflowName))
    return plugin
  }
}

export const createConversationHistory = (task: Task) => {
  // Check if the task is a Chat
  if (task.parameters?.chat) {
    return {
      id: task.id,
      prompt: task.parameters?.prompts ?? task.parameters?.chat?.prompt ?? '',
      status: task.status ? (task.status as TaskStatus) : TaskStatus.running,
      type: ConversationType.Chat,
      referenceImageId: task.parameters?.mediaId as string,
      outputImageId: task.outputs?.mediaId as string,
      creditCost: task.paidCredit,
      rawTask: task,
    } as ConversationHistory
  }

  // Check if the task is an Enhance
  if (task.parameters?.model === 'pixai-panelplugin') {
    return {
      id: task.id,
      prompt: '',
      status: task.status ? (task.status as TaskStatus) : TaskStatus.running,
      type: ConversationType.Enhance,
      referenceImageId: task.parameters?.inputs?.image?.media_id as string,
      outputImageId: task.outputs?.mediaId as string,
      creditCost: task.paidCredit,
      rawTask: task,
    } as ConversationHistory
  }

  // Check if the task is an Upscale
  if (task.parameters?.enlarge || task.parameters?.upscale) {
    return {
      id: task.id,
      prompt: '',
      status: task.status ? (task.status as TaskStatus) : TaskStatus.running,
      type: ConversationType.Upscale,
      referenceImageId: task.parameters?.mediaId as string,
      outputImageId: task.outputs?.mediaId as string,
      creditCost: task.paidCredit,
      rawTask: task,
    } as ConversationHistory
  }

  return undefined
}

// Cache for the lookup maps to avoid rebuilding them on every call
let cachedHistoryFingerprint = '' // Mutable because we update it when history changes
let cachedTasksByOutputImage: Record<string, ConversationHistory> = {} // Mutable to clear and rebuild
let cachedTasksByReferenceImage: Record<string, ConversationHistory[]> = {} // Mutable to clear and rebuild

export const calculateTaskChain = (
  currentTask: ConversationHistory,
  historyItems: ConversationHistory[],
): string[] => {
  if (currentTask.id === '0') {
    return historyItems.map(item => item.id)
  }
  if (historyItems.length === 0) {
    return []
  }
  if (historyItems.length === 1) {
    return [historyItems[0].id]
  }

  // Generate a fingerprint to detect changes in the history items
  const lastId = historyItems[historyItems.length - 1].id
  const lastOutputId = historyItems[historyItems.length - 1].outputImageId
  const currentFingerprint = `${historyItems[0].id}-${lastId}-${lastOutputId}`

  // Rebuild the maps if the fingerprint has changed
  if (cachedHistoryFingerprint !== currentFingerprint) {
    // Create a map for quick lookup of tasks by their output image ID
    cachedTasksByOutputImage = {}

    // Create a map for quick lookup of tasks by their reference image ID
    cachedTasksByReferenceImage = {}

    // Initialize these maps from history items
    historyItems.forEach(task => {
      if (task.outputImageId) {
        cachedTasksByOutputImage[task.outputImageId] = task
      }

      if (task.referenceImageId) {
        if (!cachedTasksByReferenceImage[task.referenceImageId]) {
          cachedTasksByReferenceImage[task.referenceImageId] = []
        }
        cachedTasksByReferenceImage[task.referenceImageId].push(task)
      }
    })

    // Update the cached fingerprint
    cachedHistoryFingerprint = currentFingerprint
  }

  // Start with the current task
  const taskChain: ConversationHistory[] = [currentTask]

  // Look backwards: find predecessors by following the referenceImageId -> outputImageId link
  let predecessor = currentTask
  while (
    predecessor.referenceImageId &&
    cachedTasksByOutputImage[predecessor.referenceImageId]
  ) {
    predecessor = cachedTasksByOutputImage[predecessor.referenceImageId]
    // Insert at the beginning of the chain
    taskChain.unshift(predecessor)
  }

  // Look forwards: find successors by following the outputImageId -> referenceImageId link
  let successor = currentTask
  while (
    successor.outputImageId &&
    cachedTasksByReferenceImage[successor.outputImageId]?.length
  ) {
    // Find the first task that references our output image
    // In case of multiple references, we take the first one by default
    successor = cachedTasksByReferenceImage[successor.outputImageId][0]
    // Add to the end of the chain
    taskChain.push(successor)
  }

  // Convert the task objects to just their IDs for the final result
  return taskChain.map(task => task.id)
}

export const createWorkspace = async (
  initialImageId: string,
  title?: string,
) => {
  track('chat_workspace_create_click')
  const resp = await executeCreateChatWorkspace({
    initialImage: initialImageId,
    title: title?.trim().length === 0 ? undefined : title,
  })

  return resp.data?.createChatWorkspace?.id
}
